import { Link } from "react-router-dom";

const SECTIONS = [
  {
    id: "information-we-collect",
    title: "1. Information We Collect",
    content: [
      {
        heading: "Information you provide directly",
        body: "When you register for a webinar, request a demo, or contact us, we collect information such as your first and last name, work email address, phone number, and company name. This information is submitted voluntarily through forms on our website.",
      },
      {
        heading: "Usage and technical data",
        body: "We automatically collect certain technical information when you visit imperium.chat, including your IP address, browser type, operating system, referring URLs, pages viewed, and time spent on pages. This data helps us understand how visitors use our site and improve the experience.",
      },
      {
        heading: "Communications data",
        body: "If you contact us by email or through a contact form, we retain records of that correspondence, including any information you choose to include in your message.",
      },
    ],
  },
  {
    id: "how-we-use",
    title: "2. How We Use Your Information",
    content: [
      {
        heading: "To provide and improve our services",
        body: "We use your information to send webinar confirmation and reminder emails, respond to inquiries, deliver demo calls you request, and improve the functionality and content of our platform.",
      },
      {
        heading: "Marketing communications",
        body: "With your consent, we may send you information about Imperium products, features, and industry news. You can opt out of marketing emails at any time using the unsubscribe link in any email we send.",
      },
      {
        heading: "Business operations",
        body: "We may use your information to analyze usage trends, monitor the performance of our services, detect and prevent fraud or abuse, and comply with legal obligations.",
      },
    ],
  },
  {
    id: "tcpa",
    title: "3. TCPA Compliance",
    content: [
      {
        heading: "Telephone Consumer Protection Act",
        body: "Imperium's platform is designed to help title companies and mortgage professionals conduct AI-powered outbound calling. All calls initiated through the Imperium platform are the responsibility of the subscribing business. Imperium requires all customers to comply with the Telephone Consumer Protection Act (TCPA) and all applicable federal and state telemarketing laws.",
      },
      {
        heading: "Your consent",
        body: "By submitting your phone number on any form on imperium.chat, you expressly consent to receive calls and text messages from Imperium at the number provided, including calls made using an automatic telephone dialing system or an artificial or prerecorded voice. You understand that consent is not a condition of purchase. Message and data rates may apply.",
      },
      {
        heading: "Opt-out",
        body: "You may opt out of receiving calls or text messages from us at any time by replying STOP to any text message, telling a live agent you wish to be removed, or contacting us at the address below. We will honor all opt-out requests promptly.",
      },
    ],
  },
  {
    id: "sharing",
    title: "4. How We Share Your Information",
    content: [
      {
        heading: "We do not sell your data",
        body: "Imperium does not sell, rent, or trade your personal information to third parties for their own marketing purposes.",
      },
      {
        heading: "Service providers",
        body: "We work with trusted third-party vendors who assist us in operating our website and delivering our services — including email delivery, analytics, and cloud infrastructure providers. These vendors are contractually prohibited from using your data for any purpose other than providing services to Imperium.",
      },
      {
        heading: "Legal requirements",
        body: "We may disclose your information if required to do so by law, regulation, or legal process, or if we believe disclosure is necessary to protect the rights, property, or safety of Imperium, our customers, or the public.",
      },
    ],
  },
  {
    id: "retention",
    title: "5. Data Retention",
    content: [
      {
        heading: "How long we keep your data",
        body: "We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, maintain our business records, comply with legal obligations, and resolve any disputes. Webinar registration data is retained for up to 24 months. You may request deletion of your data at any time.",
      },
    ],
  },
  {
    id: "your-rights",
    title: "6. Your Rights",
    content: [
      {
        heading: "Access and correction",
        body: "You have the right to request a copy of the personal information we hold about you and to request corrections if that information is inaccurate or incomplete.",
      },
      {
        heading: "Deletion",
        body: "You may request that we delete your personal information. We will honor deletion requests subject to any legal obligations that require us to retain certain records.",
      },
      {
        heading: "California residents",
        body: "If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect, the right to delete, and the right to opt out of the sale of personal information (we do not sell personal information). To exercise these rights, contact us at the address below.",
      },
    ],
  },
  {
    id: "security",
    title: "7. Security",
    content: [
      {
        heading: "How we protect your data",
        body: "Imperium is SOC 2 Type II certified. We implement industry-standard technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These include encryption in transit and at rest, access controls, and regular security reviews. No method of transmission over the internet is 100% secure, however, and we cannot guarantee absolute security.",
      },
    ],
  },
  {
    id: "third-party",
    title: "8. Third-Party Links",
    content: [
      {
        heading: "External websites",
        body: "Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review the privacy policies of any third-party sites you visit.",
      },
    ],
  },
  {
    id: "changes",
    title: "9. Changes to This Policy",
    content: [
      {
        heading: "Updates",
        body: "We may update this Privacy Policy from time to time. When we do, we will revise the effective date at the top of this page. We encourage you to review this policy periodically to stay informed about how we are protecting your information. Continued use of our services after changes become effective constitutes acceptance of the revised policy.",
      },
    ],
  },
  {
    id: "contact",
    title: "10. Contact Us",
    content: [
      {
        heading: "Questions or requests",
        body: "If you have questions about this Privacy Policy or wish to exercise your rights, please contact us at: privacy@imperium.chat. We will respond to all legitimate requests within 30 days.",
      },
    ],
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      <title>Privacy Policy | Imperium</title>
      <meta name="description" content="Imperium's Privacy Policy — how we collect, use, and protect your information when you use imperium.chat or register for a webinar." />
      <link rel="canonical" href="https://imperium.chat/privacy" />
      <meta name="robots" content="noindex, follow" />

      {/* Simple navbar */}
      <nav className="border-b border-gray-100 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-navy font-extrabold text-xl tracking-tight whitespace-nowrap">
            IMPERIUM
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-medium text-[#4a5568] hover:text-navy transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm" />
            </span>
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-[#f8fafc] border-b border-gray-100 py-10 md:py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-5">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shield-check-line text-primary text-xs" />
            </span>
            <span className="text-xs font-semibold text-primary">Legal</span>
          </div>
          <h1 className="text-navy font-extrabold text-3xl sm:text-4xl mb-4 leading-tight">Privacy Policy</h1>
          <p className="text-[#4a5568] text-base leading-relaxed mb-3">
            This Privacy Policy describes how Imperium AI, Inc. (&quot;Imperium,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects,
            uses, and shares information about you when you visit <strong>imperium.chat</strong>, register for a webinar,
            or otherwise interact with our services.
          </p>
          <p className="text-[#4a5568] text-sm">
            <strong>Effective date:</strong> March 23, 2026 &nbsp;·&nbsp;
            <strong>Last updated:</strong> March 23, 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">

        {/* Quick nav */}
        <div className="bg-[#f8fafc] border border-gray-200 rounded-2xl p-5 sm:p-6 mb-8 md:mb-14">
          <p className="text-xs font-semibold text-navy uppercase tracking-widest mb-4">Contents</p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-sm text-[#4a5568] hover:text-primary transition-colors cursor-pointer"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </div>

        {/* Sections */}
        <div className="flex flex-col gap-10 md:gap-14">
          {SECTIONS.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-24">
              <h2 className="text-navy font-bold text-xl mb-6 pb-3 border-b border-gray-100">
                {section.title}
              </h2>
              <div className="flex flex-col gap-6">
                {section.content.map((block) => (
                  <div key={block.heading}>
                    <h3 className="text-navy font-semibold text-sm mb-2">{block.heading}</h3>
                    <p className="text-[#4a5568] text-sm leading-relaxed">{block.body}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 md:mt-16 pt-8 md:pt-10 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <p className="text-navy font-semibold text-sm mb-1">Questions about this policy?</p>
            <p className="text-[#4a5568] text-sm">
              Email us at{" "}
              <a
                href="mailto:privacy@imperium.chat"
                className="text-primary hover:underline cursor-pointer"
              >
                privacy@imperium.chat
              </a>
            </p>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-navy/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm" />
            </span>
            Back to Imperium
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-4 sm:px-6 lg:px-8 bg-[#f8fafc]">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#4a5568]">
            &copy; {new Date().getFullYear()} Imperium AI, Inc. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs text-primary font-medium whitespace-nowrap">Privacy Policy</Link>
            <Link to="/" className="text-xs text-[#4a5568] hover:text-navy transition-colors whitespace-nowrap">Home</Link>
            <Link to="/webinar" className="text-xs text-[#4a5568] hover:text-navy transition-colors whitespace-nowrap">Webinar</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
